import {
	Banner,
	Button,
	Checkbox,
	Form,
	ProgressBar,
	TextField,
	TextStyle,
	Toast,
} from "@shopify/polaris"
import { usePostHog } from "posthog-js/react"
import React, { useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { BackgroundJobBanner } from "../components/BackgroundJobBanner.jsx"
import { CRXSettingsModal } from "../components/CRXSettings.jsx"
import { CRXSuggestionErrorBanner } from "../components/CRXSuggestionErrorBanner.jsx"
import { CRXSuggestionInfoBanner } from "../components/CRXSuggestionInfoBanner.jsx"
import Logo from "../components/Logo"
import { ProductPicker } from "../components/ProductPicker.jsx"
import { ScopesUpdateTutorial } from "../components/ScopesUpdateTutorial.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { Video } from "../components/Video.jsx"
import { CreatePageErrorBanner } from "../components/ai/CreatePageErrorBanner.jsx"
import { CreditsCostPreview } from "../components/ai/CreditsCostPreview.jsx"
import { KopyPagesPreview } from "../components/ai/KopyPagesPreview.jsx"
import { ProductAngle } from "../components/ai/ProductAngle.jsx"
import {
	ThemeColorPalettePicker,
	themeColorPalettes,
} from "../components/ai/ThemeColorPalettePicker.jsx"
import { ThemeInstallationInfoBanner } from "../components/ai/ThemeInstallationInfoBanner.jsx"
import { ThemePicker } from "../components/ai/ThemePicker.jsx"
import { TranslateButton } from "../components/ai/TranslateButton.jsx"
import { AIStateContext } from "../components/providers/AIStateProvider.jsx"
import { BackgroundJobsStateContext } from "../components/providers/BackgroundJobsStateProvider.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider"
import { SupportedPlatformsIcons } from "../components/single/SupportedPlatformsIcons.jsx"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch"
import { useFirestoreDataUpdate } from "../hooks/useFirestoreDataUpdate.js"
import { useFirestoreImportHistoryUpdate } from "../hooks/useFirestoreImportHistoryUpdate.js"
import { useGQL } from "../hooks/useGQL.js"
import { usePrepareAndImportProduct } from "../hooks/usePrepareAndImportProduct.js"
import { useStateRef } from "../hooks/useStateRef.js"
import { formatFetchedProduct } from "../utils/formatFetchedProduct.js"
import { getOptionImages } from "../utils/getOptionImages.js"
import { validateUrl } from "../utils/validateUrl"
import {
	buildTranslatedProductOptions,
	translateVariantOptionsByProductOptions,
} from "../utils/variantOptions.js"
import {
	canModifyKopyPagesThemeColors,
	getAutostorePagesThemeName,
	isAutostoreDefaultThemeSelection,
	isAutostoreSpecificTheme,
} from "../utils/kopyPagesThemes.js"
import {
	DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY,
	hasEnoughKopyPagesImages,
	loadKopyPagesProductResult,
} from "../utils/kopyPagesProductResult.js"

// 1400 to 1500 input tokens + 400 to 500 output tokens (gpt-4o) = 70 kopy credits +-15%
const ESTIMATED_CREDITS_COST = 100

const Pages = () => {
	const posthog = usePostHog()
	const { state } = useContext(GlobalStateContext)
	const { aiState } = useContext(AIStateContext)
	const authenticatedFetch = useAuthenticatedFetch()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const prepareAndImportProduct = usePrepareAndImportProduct()
	const updateImportHistory = useFirestoreImportHistoryUpdate()
	const gql = useGQL()
	const { backgroundJobsState } = useContext(BackgroundJobsStateContext)
	const [searchParams, setSearchParams] = useSearchParams()

	const [hasNeccessaryScopes, setHasNeccessaryScopes] = useState(null)

	const [loading, setLoading, loadingRef] = useStateRef(false)

	const [progress, setProgress] = useState(null)
	const [productUrl, setProductUrl, productUrlRef] = useStateRef("")
	const [isUrlValid, setIsUrlValid] = useState(true)
	const [pickedProductId, setPickedProductId] = useState(null)
	const [pickedProductName, setPickedProductName] = useState(null)

	const [showCrxSuggestionInfoBanner, setShowCrxSuggestionInfoBanner] =
		useState(false)
	const [showCrxSuggestionErrorBanner, setShowCrxSuggestionErrorBanner] =
		useState(false)
	const [CRXModalIsOpen, setCRXModalIsOpen] = useState(false)

	const [fetchError, setFetchError] = useState("")
	const [uploadError, setUploadError] = useState("")
	const [createPageError, setCreatePageError] = useState("")
	const [createPageErrorIsFromPreview, setCreatePageErrorIsFromPreview] =
		useState(false)

	const [fetchedProduct, setFetchedProduct] = useState(null)
	const [fetchedProductCurrency, setFetchedProductCurrency] = useState(
		DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY,
	)

	const [showBackgroundJobBanner, setShowBackgroundJobBanner] =
		useState(false)

	const [success, setSuccess] = useState(false)
	const [successfulProductUrl, setSuccessfulProductUrl] = useState(null)
	const [language, setLanguage] = useState("en")
	const [productAngle, setProductAngle] = useState(null)

	const [productReadyFromBackgroundJob, setProductReadyFromBackgroundJob] =
		useState(false)

	const [processStep, setProcessStep] = useState(1)

	const previewStateRef = useRef({
		show: false,
	})
	const handledImportUrlRef = useRef(null)
	const handledResultIdRef = useRef(null)
	const [showPreview, setShowPreview] = useState(false)
	const [previewPage, setPreviewPage] = useState("product")
	const [previewData, setPreviewData] = useState(defaultPreviewData)
	const [colorPalette, setColorPalette] = useState("pink")
	const [themeIdSelected, setThemeIdSelected] = useState(null)
	const [publishThemeSelected, setPublishThemeSelected] = useState(false)
	const [modifyThemeColors, setModifyThemeColors] = useState(false)
	const [createHomepageSelected, setCreateHomepageSelected] = useState(true)
	const formContainerRef = useRef(null)

	const changeProductURL = (value) => {
		setProcessStep(1)
		setCreateHomepageSelected(true)
		setPickedProductName(null)
		setProductReadyFromBackgroundJob(false)
		const validUrl = validateUrl(value)
		if (validUrl) {
			setProductUrl(value)
			setIsUrlValid(true)
		} else {
			setProductUrl(value)
			setIsUrlValid(false)
		}
	}

	const changeThemeSelected = (themeId) => {
		setThemeIdSelected(themeId)

		const selectedTheme = state.storeThemes?.find(
			(theme) => theme.id === themeId,
		)

		if (isAutostoreSpecificTheme(selectedTheme)) {
			setColorPalette(getAutostorePagesThemeName(selectedTheme))
		} else if (
			selectedTheme?.themePalette &&
			themeColorPalettes[selectedTheme.themePalette]
		) {
			setColorPalette(selectedTheme.themePalette)
		}
	}

	const getProductFromProductId = async (productId) => {
		let product = null

		const res = await gql(`{
			product(id: "${productId}") {
				title
				description
				variants(first: 50) {
					nodes {
						price
						compareAtPrice
						selectedOptions {
							name
							value
						}
						media(first: 1) {
							nodes {
								id
							}
						}
					}
				}
				options {
					name
					values
				}
				media(first: 50) {
					nodes {
						id
						mediaContentType
						preview {
							image {
								url
							}
						}
					}
				}
			}
		}`)

		if (res?.data?.product?.media?.nodes?.length) {
			product = {
				title: res.data.product.title,
				description: res.data.product.description,
				variants: res.data.product.variants.nodes.map((variant) => {
					const variantImageId = variant.media?.nodes?.[0]?.id
					return {
						price: variant.price,
						compareAtPrice: variant.compareAtPrice,
						option1: variant.selectedOptions?.[0]?.value || "",
						option2: variant.selectedOptions?.[1]?.value || "",
						option3: variant.selectedOptions?.[2]?.value || "",
						imageSrc: variantImageId
							? res.data.product.media.nodes.find(
									(node) => node.id === variantImageId,
								)?.preview?.image?.url || null
							: null,
					}
				}),
				options: res.data.product.options.map((option) => {
					return {
						name: option.name,
						values: option.values,
					}
				}),
				images: res.data.product.media.nodes.map((node) => {
					if (node?.mediaContentType === "IMAGE") {
						return { src: node?.preview?.image?.url || null }
					} else {
						return null
					}
				}),
			}
		}

		return product
	}

	const getProcessedImagesStatus = async (productId) => {
		let hasProcessedImages = false

		const res = await gql(`{
			product(id: "${productId}") {
				media(first: 10) {
					nodes {
						status
					}
				}
			}
		}`)

		if (res?.data?.product?.media?.nodes?.length) {
			hasProcessedImages = res.data.product.media.nodes.every(
				(node) => node?.status === "READY",
			)
		}

		return hasProcessedImages
	}

	const checkIfImagesAreProcessed = async (productId, tryN = 1) => {
		const hasProcessedImages = await getProcessedImagesStatus(productId)
		if (hasProcessedImages) {
			return true
		} else if (tryN <= 5 * 60) {
			await new Promise((resolve) => setTimeout(resolve, 5 * 1000))
			return await checkIfImagesAreProcessed(productId, tryN + 1)
		}
	}

	const createAIProductPage = async (
		productId,
		fromPickedProduct = false,
	) => {
		posthog?.capture("pages_ai_product_page_requested")
		setCreatePageErrorIsFromPreview(false)

		if (fromPickedProduct) {
			setLoading(true)

			const productFromPickedProduct =
				await getProductFromProductId(productId)
			if (!hasEnoughImages(productFromPickedProduct)) {
				setLoading(false)
				setProgress(null)
				setCreatePageError(t("kopy_pages.not_enough_images_error"))
				return
			}
		} else {
			setProgress(60)
			const hasProcessedImages =
				await checkIfImagesAreProcessed(productId)

			if (!hasProcessedImages) {
				setLoading(false)
				setProgress(null)
				setCreatePageError(
					"Images are not processed yet, please wait a few moments and try again.",
				)
				return
			}
		}

		setProgress(70)

		const selectedTheme = state.storeThemes?.find(
			(theme) => theme.id === themeIdSelected,
		)
		const canModifySelectedThemeColors =
			canModifyKopyPagesThemeColors(selectedTheme)
		const themeName = isAutostoreDefaultThemeSelection(colorPalette)
			? getAutostorePagesThemeName(selectedTheme)
			: colorPalette
		const isAutostoreDefaultSelection =
			isAutostoreDefaultThemeSelection(themeName)
		const shouldInstallNewTheme =
			isAutostoreSpecificTheme(selectedTheme) &&
			!isAutostoreDefaultSelection

		authenticatedFetch("/api/ai/createAIProductPage", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				themeId: shouldInstallNewTheme ? null : themeIdSelected,
				productId,
				fields: previewData.metafields,
				themeName,
				publishTheme: publishThemeSelected,
				skipPaletteDetection: isAutostoreDefaultSelection,
				modifyThemeColors:
					modifyThemeColors && canModifySelectedThemeColors,
				createHomepage:
					createHomepageSelected &&
					Boolean(previewData.metafields?.homepage),
			}),
		})
			.then((res) => res.json())
			.then((res) => {
				if (res.success === true) {
					// 3s timeout to make sure the page has all the changes made
					setTimeout(() => {
						setLoading(false)
						setProgress(null)
						setSuccess(true)
						setProcessStep(1)
						if (res.productUrl) {
							setSuccessfulProductUrl(res.productUrl)
						}
					}, 3 * 1000)
					posthog?.capture("pages_ai_product_page_created")
				} else {
					posthog?.capture("pages_ai_product_page_creation_error")
					setLoading(false)
					setProgress(null)
					if (res.error) {
						setCreatePageError(res.error)
					}
				}
			})
			.catch((err) => {
				posthog?.capture("pages_ai_product_page_creation_error")

				console.error("createAIProductPage catched error", err)
				setLoading(false)
				setProgress(null)
				setCreatePageError("An unknown error occurred")
			})
	}

	const uploadProduct = async ({ product, currency }) => {
		if (!product) {
			setLoading(false)
			setProgress(null)
			setUploadError("No product to upload")
			return
		}
		setProgress(40)

		const formattedProduct = formatFetchedProduct({
			product,
			productCurrency: currency,
			roundPrices: state.roundingEnabledByDefault,
			roundingNumber: state.roundingNumberByDefault,
			shopCurrency: state.shopCurrency,
			defaultVendor: state.defaultVendor,
			publishByDefault: state.publishByDefault,
			statusActiveByDefault: state.statusActiveByDefault,
			defaultTags: state.defaultTags,
			taxableByDefault: state.taxableByDefault,
		})

		const { data, errors } = await prepareAndImportProduct({
			product: formattedProduct,
		})

		if (errors?.length) {
			setLoading(false)
			setProgress(null)
			if (
				errors.some((err) =>
					(
						err?.graphQLErrors?.[0]?.message ||
						err?.message ||
						""
					).includes("not supported on trial accounts"),
				)
			) {
				setUploadError(
					'Uploading images is not supported on stores with Shopify trial plans. Please upgrade your Shopify store plan to at least "Basic" and try importing the product again.',
				)
			} else {
				setUploadError(
					errors
						.map((err, i) => {
							const errMessage =
								err.graphQLErrors?.[0]?.message ||
								err.message ||
								"Unspecified error 3"
							return `${i + 1}. ${errMessage}`
						})
						.join("\n"),
				)
			}
		} else if (data?.productCreate?.product?.id) {
			updateImportHistory({
				productTitle: data.productCreate.product.title || "",
				productUrl: productUrlRef.current || "",
				productImageUrl: formattedProduct.images?.[0]?.src || "",
				dateImported: new Date(),
				importMode: "pages",
				productId: data?.productCreate?.product?.id || "",
			})
		} else {
			setLoading(false)
			setProgress(null)
			setUploadError("An unknown error occurred")
		}

		return data?.productCreate?.product?.id || null
	}

	const getGeneratedMetafields = async ({
		description,
		title,
		options,
		images = [],
	}) => {
		return await authenticatedFetch("/api/ai/getGeneratedMetafields", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				language,
				description,
				title,
				options,
				images,
				productAngle: productAngle || undefined,
				generateHomepage: true,
			}),
		})
			.then((res) => res.json())
			.then((res) => {
				const metafields = res.generatedMetafields
				const usable =
					metafields &&
					typeof metafields === "object" &&
					!metafields.error &&
					Object.keys(metafields).length > 0
				return {
					metafields: usable ? metafields : null,
					error:
						res.error ||
						(usable ? null : "An unknown error occurred"),
				}
			})
			.catch((err) => {
				console.error("getGeneratedMetafields catched error", err)
				return { metafields: null, error: "An unknown error occurred" }
			})
	}

	const checkIfProductExists = (url) => {
		return authenticatedFetch("/api/productFetcher/checkIfProductExists", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ productUrl: validateUrl(url) }),
		})
			.then((res) => res.json())
			.then((res) => {
				return {
					productExists: res?.productExists || false,
					productId: res?.productId || null,
				}
			})
			.catch((err) => {
				console.error("checkIfProductExists catched error", err)
				return { productExists: false, productId: null }
			})
	}

	const hasEnoughImages = (product) => {
		return hasEnoughKopyPagesImages(product)
	}

	const resetState = () => {
		setFetchError("")
		setFetchedProduct(null)
		setFetchedProductCurrency(DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY)

		setUploadError("")
		setShowBackgroundJobBanner(false)

		setCreatePageError("")
		setCreatePageErrorIsFromPreview(false)
		setSuccess(false)
		setSuccessfulProductUrl(null)

		setPreviewData(defaultPreviewData)
		setCreateHomepageSelected(true)
	}

	const getProduct = async (url) => {
		const isCrxScrapableProduct = // Temporary. Remove after all platforms scrapers have been implemented into Kopy Chrome extension
			url.includes("aliexpress.com") ||
			url.includes("aliexpress.us") ||
			url.includes("aliexpress.ru") ||
			url.includes("shein.com")
		setLoading(true)
		setTimeout(() => {
			if (loadingRef.current === true && isCrxScrapableProduct) {
				setShowCrxSuggestionInfoBanner(true)
			}
		}, 5000)
		setProgress(10)
		setPickedProductId(null)
		setPickedProductName(null)
		setShowCrxSuggestionErrorBanner(false)
		resetState()

		const { productExists, productId } = await checkIfProductExists(url)

		if (productExists && productId) {
			setPickedProductId(productId)
			return getMetafieldsAndUpdatePreview({
				pickedProductId: productId,
				currency: state.shopCurrency,
			})
		}

		authenticatedFetch("/api/productFetcher/getProduct", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ productUrl: validateUrl(url) }),
		})
			.then((res) => res.json())
			.then((res) => {
				if (res.scrapingFailed) {
					setShowCrxSuggestionErrorBanner(true)
				}
				if (res.error) {
					setLoading(false)
					setFetchError(res.error)
					setProgress(null)
					setShowCrxSuggestionInfoBanner(false)
				} else {
					setShowCrxSuggestionInfoBanner(false)
					if (res.product) {
						if (!hasEnoughImages(res.product)) {
							setLoading(false)
							setProgress(null)
							setUploadError(
								t("kopy_pages.not_enough_images_error"),
							)
						} else {
							setFetchedProduct(res.product)
							setFetchedProductCurrency(
								res.currency ||
									DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY,
							)
							getMetafieldsAndUpdatePreview({
								product: res.product,
								currency:
									res.currency ||
									DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY,
							})
						}
					}
					if (res.callbackId) {
						setLoading(false)
						setProgress(null)
						setShowBackgroundJobBanner(true)
					}
				}
			})
			.catch((err) => {
				console.error("getProduct catched error", err)
				setShowCrxSuggestionInfoBanner(false)
				setLoading(false)
				setFetchError(t("single.fetch_error"))
				setProgress(null)
			})
	}

	const getMetafieldsAndUpdatePreview = async ({
		pickedProductId = null,
		product = null,
		currency = null,
	}) => {
		setLoading(true)
		setProgress(50)

		let formattedProduct = null
		if (pickedProductId) {
			formattedProduct = await getProductFromProductId(pickedProductId)
		} else {
			formattedProduct = product
		}

		const { metafields, error } = await getGeneratedMetafields({
			description:
				formattedProduct.body_html || formattedProduct.description,
			title: formattedProduct.title,
			images: (formattedProduct.images || [])
				.map((image) => image?.src)
				.filter(Boolean)
				.slice(0, 5),
			options: formattedProduct.options.map((option, optionIndex) => {
				return {
					name: option.name,
					values: option.values,
					images: getOptionImages({
						images: formattedProduct.images,
						variants: formattedProduct.variants,
						optionIndex,
					}),
				}
			}),
		})

		setLoading(false)
		setProgress(null)
		if (error) {
			setCreatePageErrorIsFromPreview(true)
			setCreatePageError(error)
			return
		}
		setProcessStep(2)
		setPreviewData({
			product: product || formattedProduct,
			currency: currency || state.shopCurrency,
			metafields,
		})
	}

	const onProductPicked = async ({ productId, productName }) => {
		setFetchError("")
		setUploadError("")
		setCreatePageError("")
		setCreatePageErrorIsFromPreview(false)
		setPickedProductId(productId)
		setPickedProductName(productName)
		setCreateHomepageSelected(true)
	}

	const onSubmitButtonClick = async () => {
		if (processStep === 1) {
			if (productReadyFromBackgroundJob) {
				getMetafieldsAndUpdatePreview({
					product: fetchedProduct,
					currency: fetchedProductCurrency || state.shopCurrency,
				})
			} else if (pickedProductId) {
				getMetafieldsAndUpdatePreview({
					pickedProductId,
					currency: state.shopCurrency,
				})
			} else {
				getProduct(productUrl)
			}
		} else if (processStep === 2) {
			if (pickedProductId) {
				createAIProductPage(pickedProductId, true)
			} else {
				const translatedOptions = previewData.metafields?.options
				const productWithTranslations = {
					...fetchedProduct,
					title:
						previewData.metafields?.productInformation?.title ||
						fetchedProduct.title,
					options: translatedOptions
						? buildTranslatedProductOptions({
								sourceOptions: fetchedProduct.options,
								translatedOptions,
							})
						: fetchedProduct.options,
					variants: fetchedProduct.variants.map((v) => ({
						...v,
						...translateVariantOptionsByProductOptions({
							variant: v,
							sourceOptions: fetchedProduct.options,
							translatedOptions,
						}),
					})),
				}

				const productId = await uploadProduct({
					product: productWithTranslations,
					currency:
						previewData.currency ||
						fetchedProductCurrency ||
						state.shopCurrency,
				})
				if (!productId) return
				createAIProductPage(productId, false)
			}
		}
	}

	useEffect(() => {
		if (!state.hasValidKopyPlusSubscription) {
			navigate("/upgradeplus")
		}
	}, [])

	useEffect(() => {
		const backgroundJobs = backgroundJobsState.jobs

		if (searchParams.get("productHash") && backgroundJobs.length > 0) {
			const productHash = searchParams.get("productHash")
			const job = backgroundJobs.find((job) => job.id === productHash)

			const nextSearchParams = new URLSearchParams(searchParams)
			nextSearchParams.delete("productHash")
			setSearchParams(nextSearchParams, { replace: true })

			if (job?.product) {
				if (!hasEnoughImages(job.product)) {
					setLoading(false)
					setProgress(null)
					setUploadError(t("kopy_pages.not_enough_images_error"))
				} else {
					setFetchedProduct(job.product)
					setFetchedProductCurrency(
						job.currency || DEFAULT_KOPY_PAGES_PRODUCT_CURRENCY,
					)
					setProductUrl(job.productUrl || "")
					setPickedProductName(job.product.title)
					setPickedProductId(null)
					setProductReadyFromBackgroundJob(true)
				}
			}
		}
	}, [backgroundJobsState.jobs, searchParams])

	// Kopy Chrome Extension functionality
	useEffect(() => {
		const importUrl = searchParams.get("importUrl")
		const resultId = searchParams.get("resultId")

		if (importUrl && handledImportUrlRef.current !== importUrl) {
			handledImportUrlRef.current = importUrl
			setProductUrl(importUrl)
			getProduct(importUrl)

			const nextSearchParams = new URLSearchParams(searchParams)
			nextSearchParams.delete("importUrl")
			setSearchParams(nextSearchParams, { replace: true })
			return
		}

		if (!resultId || handledResultIdRef.current === resultId) return

		handledResultIdRef.current = resultId
		setLoading(true)
		setProgress(10)

		const nextSearchParams = new URLSearchParams(searchParams)
		nextSearchParams.delete("resultId")
		setSearchParams(nextSearchParams, { replace: true })

		const loadResult = async () => {
			const result = await authenticatedFetch(
				`/api/crx/getCrxProductScrapingResult?resultId=${resultId}`,
			)
				.then((res) => res.json())
				.catch((err) => {
					console.error(
						"getCrxProductScrapingResult catched error",
						err,
					)
					return null
				})

			if (result) {
				await loadKopyPagesProductResult({
					result,
					getMetafieldsAndUpdatePreview,
					setFetchedProduct,
					setFetchedProductCurrency,
					setProductUrl,
					setLoading,
					setProgress,
					setUploadError,
					t,
				})
			} else {
				setLoading(false)
				setProgress(null)
				setFetchError(t("single.fetch_error"))
			}
		}

		loadResult()
	}, [searchParams])

	useEffect(() => {
		const update = () => {
			if (!formContainerRef.current) return

			const currentShow = previewStateRef.current.show

			const formContainerRect =
				formContainerRef.current.getBoundingClientRect()

			if (!currentShow) {
				setShowPreview(true)
			}

			if (currentShow) {
				const previewContainer = document.getElementById(
					"kopy-pages-preview-container",
				)
				if (previewContainer) {
					previewContainer.style.top = "150px"
					previewContainer.style.left =
						formContainerRect.right + 25 + "px"
				}
			}

			previewStateRef.current = {
				show: true,
			}
		}

		const interval = setInterval(() => {
			update()
		}, 13)

		return () => {
			clearInterval(interval)
		}
	}, [])

	useEffect(() => {
		if (state.storeThemes?.length > 0) {
			const compatibleAuthors = [
				"AutoStore.ai",
				"LuminTheme",
				"KopyTheme",
			].map((author) => author.toLowerCase())
			const activeTheme = state.storeThemes.find(
				(theme) =>
					theme.role === "MAIN" &&
					compatibleAuthors.includes(theme.author?.toLowerCase()),
			)
			if (activeTheme) {
				setThemeIdSelected(activeTheme.id)
				if (isAutostoreSpecificTheme(activeTheme)) {
					setColorPalette(getAutostorePagesThemeName(activeTheme))
					return
				}
				// Set detected theme palette as default
				if (
					activeTheme.themePalette &&
					themeColorPalettes[activeTheme.themePalette]
				) {
					setColorPalette(activeTheme.themePalette)
				}
			} else {
				// No compatible theme found, set to null
				setThemeIdSelected(null)
			}
		}
	}, [state.storeThemes])

	useEffect(() => {
		// Reset modifyThemeColors when theme or palette changes
		const selectedTheme = state.storeThemes?.find(
			(theme) => theme.id === themeIdSelected,
		)
		const themeCurrentPalette = selectedTheme?.themePalette || null
		const isPaletteChanged = isAutostoreSpecificTheme(selectedTheme)
			? !isAutostoreDefaultThemeSelection(colorPalette)
			: themeCurrentPalette && themeCurrentPalette !== colorPalette
		const canModifySelectedThemeColors =
			canModifyKopyPagesThemeColors(selectedTheme)

		if (isPaletteChanged && canModifySelectedThemeColors) {
			setModifyThemeColors(true)
		} else {
			setModifyThemeColors(false)
		}
	}, [themeIdSelected, colorPalette, state.storeThemes])

	useEffect(() => {
		const selectedTheme = state.storeThemes?.find(
			(theme) => theme.id === themeIdSelected,
		)

		if (isAutostoreSpecificTheme(selectedTheme)) {
			const autostoreThemeName = getAutostorePagesThemeName(selectedTheme)

			if (autostoreThemeName && colorPalette !== autostoreThemeName) {
				setColorPalette(autostoreThemeName)
			}
		}
	}, [themeIdSelected, state.storeThemes])

	if (!(hasNeccessaryScopes === true)) {
		return (
			<div>
				<TopMenu
					goBack="/"
					storeSelector
					storeSelectorDisabled={loading}
				/>
				<div
					style={{
						paddingTop: "3.5rem",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<div style={{ maxWidth: "40rem", width: "100%" }}>
						<div style={{ marginTop: "3rem" }}>
							<ScopesUpdateTutorial
								context={"pages"}
								requiredScopes={[
									"write_themes",
									"read_themes",
									"write_metaobjects",
									"read_metaobjects",
									"write_metaobject_definitions",
									"read_metaobject_definitions",
									"write_translations",
									"read_translations",
								]}
								hasNeccessaryScopes={hasNeccessaryScopes}
								onRequiredScopesChecked={() =>
									setHasNeccessaryScopes(true)
								}
								videoSrc="https://kopy.b-cdn.net/kopy_pages_scopes_tutorial.webm"
							/>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const insufficientCredits =
		aiState.creditsAvailable < ESTIMATED_CREDITS_COST

	return (
		<div>
			<TopMenu goBack="/" storeSelector storeSelectorDisabled={loading} />

			<div
				style={{
					paddingTop: "3.5rem",
					paddingBottom: "1rem",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Logo />

				<div
					ref={formContainerRef}
					style={{ maxWidth: "40rem", width: "100%" }}
				>
					<div style={{ marginTop: "3rem" }}>
						<Form
							onSubmit={
								!loading && isUrlValid && !insufficientCredits
									? () => getProduct(productUrl)
									: () => void 0
							}
						>
							<div
								style={{
									display: "flex",
									gap: "0.5rem",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<span style={{ fontSize: "0.85rem" }}>
									{t("single.product_url")}
								</span>
								<SupportedPlatformsIcons />
							</div>

							<div style={{ marginTop: "0.5rem" }}>
								<TextField
									inputMode="url"
									clearButton={!loading}
									onClearButtonClick={() => {
										changeProductURL("")
										setFetchedProduct(null)
										setPickedProductName(null)
										setPickedProductId(null)
										setProductReadyFromBackgroundJob(false)
									}}
									placeholder="[store].com/[product]"
									value={pickedProductName || productUrl}
									onChange={changeProductURL}
									connectedRight={
										<Button
											primary={true}
											onClick={onSubmitButtonClick}
											loading={loading}
											disabled={
												loading ||
												(!isUrlValid &&
													!pickedProductId &&
													!productReadyFromBackgroundJob) ||
												(productUrl === "" &&
													!pickedProductId &&
													!productReadyFromBackgroundJob) ||
												insufficientCredits
											}
										>
											{processStep === 1
												? t("ai.view_page_preview")
												: processStep === 2
													? t(
															"ai.create_product_page",
														)
													: null}
										</Button>
									}
								/>
							</div>

							{progress == null && (
								<div
									style={{
										display: "flex",
										gap: "0.2rem",
										alignItems: "center",
									}}
								>
									<div
										style={{
											marginTop: "0.5rem",
											display: "flex",
											flexDirection: "column",
											gap: "0.1rem",
											paddingLeft: "1rem",
										}}
									>
										{processStep === 1 && (
											<TranslateButton
												language={language}
												onLanguageSelect={(language) =>
													setLanguage(language)
												}
												disabled={loading}
											/>
										)}
										{processStep === 1 && (
											<ProductAngle
												productAngle={productAngle}
												onAngleChange={(angle) =>
													setProductAngle(angle)
												}
												disabled={loading}
											/>
										)}
										<ThemePicker
											themeIdSelected={themeIdSelected}
											onThemeSelected={
												changeThemeSelected
											}
											publishThemeSelected={
												publishThemeSelected
											}
											setPublishThemeSelected={
												setPublishThemeSelected
											}
											disabled={loading}
										/>
										<ThemeColorPalettePicker
											colorPalette={colorPalette}
											onPaletteSelected={(palette) =>
												setColorPalette(palette)
											}
											themeIdSelected={themeIdSelected}
											modifyThemeColors={
												modifyThemeColors
											}
											setModifyThemeColors={
												setModifyThemeColors
											}
											disabled={loading}
										/>
										{processStep === 2 &&
											previewData.metafields
												?.homepage && (
												<div
													style={{
														marginTop: "0.45rem",
														padding:
															"0.65rem 0.75rem",
														background: "#fff",
														border: "1px solid #d9d9d9",
														borderRadius: "0.65rem",
														boxShadow:
															"0 6px 18px rgba(0, 0, 0, 0.06)",
														maxWidth: "25rem",
													}}
												>
													<Checkbox
														label={
															<span
																style={{
																	fontSize:
																		"0.85rem",
																	fontWeight: 650,
																}}
															>
																{t(
																	"kopy_pages.create_homepage_too",
																)}
															</span>
														}
														checked={
															createHomepageSelected
														}
														onChange={(checked) =>
															setCreateHomepageSelected(
																checked,
															)
														}
														disabled={loading}
													/>
													<p
														style={{
															marginTop:
																"0.35rem",
															marginLeft:
																"1.7rem",
															fontSize: "0.78rem",
															lineHeight: 1.35,
															color: "#666",
														}}
													>
														{t(
															"kopy_pages.create_homepage_help",
														)}
													</p>
												</div>
											)}
									</div>
									{processStep === 2 && (
										<div
											style={{
												marginLeft: "auto",
												marginRight: "1.5rem",
												marginTop: "0.8rem",
												alignSelf: "start",
												width: "fit-content",
												position: "relative",
											}}
										>
											<div
												style={{
													position: "absolute",
													top: "-1rem",
													left: "50%",
													right: "50%",
												}}
											>
												<svg
													style={{
														width: "0.5rem",
														height: "0.5rem",
														color: insufficientCredits
															? "#ff0d0d"
															: "#111",
													}}
													viewBox="0 0 24 24"
												>
													<g
														fill="none"
														fillRule="evenodd"
													>
														<path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"></path>
														<path
															fill="currentColor"
															d="M10.7 3.148a1.5 1.5 0 0 1 2.599 0l8.634 14.954a1.5 1.5 0 0 1-1.299 2.25H3.366a1.5 1.5 0 0 1-1.299-2.25l8.634-14.954Z"
														></path>
													</g>
												</svg>
											</div>
											<CreditsCostPreview
												context="pages"
												costEstimation={
													ESTIMATED_CREDITS_COST
												}
												hideEstimationTilde={true}
											/>
										</div>
									)}
								</div>
							)}

							{!isUrlValid && !pickedProductName ? (
								<div style={{ marginTop: "1rem" }}>
									<TextStyle variation="negative">
										{t("single.url_is_invalid")}
									</TextStyle>
								</div>
							) : (
								""
							)}

							{progress === null &&
								!productReadyFromBackgroundJob &&
								processStep === 1 && (
									<div style={{ marginTop: "2rem" }}>
										<ProductPicker
											onProductPicked={({ id, name }) => {
												onProductPicked({
													productId: id,
													productName: name,
												})
											}}
										/>
									</div>
								)}
						</Form>

						{progress !== null && (
							<div
								style={{
									marginTop: "1rem",
									maxWidth: "60rem",
									width: "100%",
								}}
							>
								<ProgressBar size="small" progress={progress} />
								<p
									style={{
										textAlign: "right",
										fontSize: "0.9rem",
									}}
								>
									{progress === 10
										? t("kopy_pages.getting_product")
										: progress === 40
											? t("kopy_pages.uploading_product")
											: progress === 50
												? t(
														"kopy_pages.generating_preview",
													)
												: progress === 60
													? t(
															"kopy_pages.processing_images",
														)
													: progress === 70
														? t(
																"kopy_pages.creating_product_page",
															)
														: null}
								</p>
							</div>
						)}

						{fetchError &&
							fetchError !== "" &&
							!showCrxSuggestionErrorBanner && (
								<div style={{ marginTop: "1rem" }}>
									<Banner
										status="critical"
										title={t(
											"common.error_obtaining_product",
										)}
									>
										{fetchError ===
										"is_temu_collection_or_store_url"
											? t(
													"single.temu_collection_or_store_url",
												)
											: t("single.fetch_error_display")}
									</Banner>
								</div>
							)}

						{uploadError && uploadError !== "" && (
							<div style={{ marginTop: "2rem" }}>
								<Banner
									title={t("single.error_importing_product")}
									action={{
										content: t("single.retry"),
										onAction: () =>
											uploadProduct({
												product: fetchedProduct,
												currency: state.shopCurrency,
											}),
									}}
									status="critical"
								>
									<p>
										{t(
											"single.an_error_ocurred_when_importing_the_product",
										)}
									</p>
									<p style={{ fontFamily: "monospace" }}>
										{uploadError}
									</p>
								</Banner>
							</div>
						)}

						{createPageError && createPageError !== "" && (
							<CreatePageErrorBanner
								createPageError={createPageError}
								isPreviewError={createPageErrorIsFromPreview}
							/>
						)}

						{progress === null && (
							<div
								style={{
									marginTop: "2rem",
									marginBottom: "2rem",
								}}
							>
								<ThemeInstallationInfoBanner
									themeIdSelected={themeIdSelected}
									publishThemeSelected={publishThemeSelected}
									setPublishThemeSelected={
										setPublishThemeSelected
									}
									colorPalette={colorPalette}
									modifyThemeColors={modifyThemeColors}
								/>
							</div>
						)}

						{showBackgroundJobBanner ? (
							<BackgroundJobBanner />
						) : null}
					</div>
				</div>
				{/* {showCrxSuggestionInfoBanner && (
					<CRXSuggestionInfoBanner
						openModal={() => setCRXModalIsOpen(true)}
					/>
				)} */}

				{showCrxSuggestionErrorBanner && (
					<div style={{ maxWidth: "40rem", width: "100%" }}>
						<CRXSuggestionErrorBanner
							openModal={() => setCRXModalIsOpen(true)}
						/>
					</div>
				)}

				<CRXSettingsModal
					open={CRXModalIsOpen}
					setOpen={setCRXModalIsOpen}
				/>

				{success && (
					<Toast
						content={t(
							"kopy_pages.product_page_created_successfully",
						)}
						onDismiss={() => setSuccess(false)}
						duration={86400000}
						action={
							successfulProductUrl
								? {
										content: t(
											"kopy_pages.view_product_page",
										),
										onAction: () =>
											window
												.open(
													successfulProductUrl,
													"_blank",
												)
												?.focus(),
									}
								: null
						}
					/>
				)}
			</div>

			{showPreview
				? createPortal(
						<div
							id="kopy-pages-preview-container"
							style={{
								position: "absolute",
								zIndex: 30,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									gap: "0.25rem",
									marginBottom: "0.35rem",
								}}
							>
								<button
									type="button"
									onClick={() => setPreviewPage("product")}
									style={{
										padding: "0.3rem 0.7rem",
										fontSize: "0.75rem",
										fontWeight: 600,
										border: "1px solid #222",
										borderRadius: "0.35rem",
										cursor: "pointer",
										background:
											previewPage === "product"
												? "#222"
												: "#fff",
										color:
											previewPage === "product"
												? "#fff"
												: "#222",
									}}
								>
									{t("kopy_pages.product_page")}
								</button>
								<button
									type="button"
									onClick={() => setPreviewPage("homepage")}
									style={{
										padding: "0.3rem 0.7rem",
										fontSize: "0.75rem",
										fontWeight: 600,
										border: "1px solid #222",
										borderRadius: "0.35rem",
										cursor: "pointer",
										background:
											previewPage === "homepage"
												? "#222"
												: "#fff",
										color:
											previewPage === "homepage"
												? "#fff"
												: "#222",
									}}
								>
									{t("kopy_pages.homepage")}
								</button>
							</div>
							<KopyPagesPreview
								colorPalette={
									themeColorPalettes[colorPalette]
										? colorPalette
										: "pink"
								}
								previewData={previewData}
								previewPage={previewPage}
							/>
						</div>,
						document.body,
					)
				: null}
		</div>
	)
}

const defaultPreviewData = {
	isDefault: true,
	currency: "USD",
	product: {
		title: "Brush",
		images: [
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/brush_2.png?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/71WHiR5mHVL._AC_SL1500.jpg?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/71sxmyqvhUL._AC_SL1500.jpg?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/719mP89dMYL._AC_SL1500.jpg?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/71Rit9aOgQL._AC_SL1500.jpg?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/71rcZG08ZaL._AC_SL1500.jpg?v=1760715161",
			},
			{
				src: "https://cdn.shopify.com/s/files/1/0574/5698/4152/files/81EJNMrSYhL._AC_SL1500.jpg?v=1760715161",
			},
		],
		variants: [
			{
				title: "Black",
				option1: "Black",
				price: 49,
				compareAtPrice: 90,
				imageSrc:
					"https://cdn.shopify.com/s/files/1/0574/5698/4152/files/brush_2.png?v=1760715161",
			},
			{
				title: "Pink",
				option1: "Pink",
				price: 49,
				compareAtPrice: 90,
				imageSrc:
					"https://cdn.shopify.com/s/files/1/0574/5698/4152/files/71WHiR5mHVL._AC_SL1500.jpg?v=1760715161",
			},
		],
		options: [{ name: "Color", values: ["Black", "Pink"] }],
	},
	metafields: {
		language: "en",
		productInformation: {
			title: "Hair Perfection in Minutes",
			subtitle: "Your All-in-One Salon Blowout at Home",
			benefits: [
				"Effortless Styling",
				"Shiny, Healthy Hair",
				"Save Time",
				"Boost Confidence",
			],
			reviewsAmount: "<amount> reviews",
			USP: {
				shippingInformation: "Fast Shipping",
				support: "24/7 Support",
				returns: "30 Days Easy Returns",
			},
			shippingInformation: {
				heading: "Shipping Information",
				content:
					"<p>We provide insured and tracked shipping for every order. The estimated delivery times after shipment are as follows: </p><ul><li>USA: 3-7 business days</li><li>UK: 7-18 business days</li><li>Australia: 7-18 business days</li><li>Canada: 7-18 business days</li><li>Europe: 7-18 business days</li><li>Rest of the world: 14-20 business days</li></ul>",
			},
			returnPolicy: {
				heading: "Return Policy",
				content:
					"<p>We have a strong affection for our products, and we believe that you will share the same sentiment. Therefore, we are providing a 30-Day Trial, free from any risks. If you are not fully satisfied with the outcome, we will reimburse you.</p>",
			},
			estimatedArrival:
				"<strong>Estimated arrival: (date)</strong> if you order today before 11 PM.",
			bundles: {
				heading: "Bundle & Save!",
				buyOne: "Buy 1",
				bundleOfTwo: "Bundle of 2",
				bundleOfThree: "Bundle of 3",
				mostPopular: "Most Popular",
				discountText: "[%] OFF",
				bottomText: "Great for you & a friend",
			},
		},
		scrollingUSP: {
			shippingInformation: "Fast Worldwide Shipping",
			support: "24/7 Support",
			returns: "Hassle-free Returns",
		},
		benefits: {
			heading: "Transform Your Hair, Transform You",
			items: [
				{
					heading: "Effortless Salon Blowout at Home",
					content:
						"Imagine stepping out with salon-perfect hair every day. This brush makes luxurious, voluminous styles incredibly easy.",
				},
				{
					heading: "Reveal Your Hair's Natural Radiance",
					content:
						"Transform dull, frizzy hair into healthy, shiny locks. Experience hair that looks and feels nourished.",
				},
				{
					heading: " reclaim Your Mornings",
					content:
						"Cut your styling time in half. Dry and style simultaneously, giving you more time for yourself.",
				},
				{
					heading: "unleash Your Inner Glow",
					content:
						"When your hair looks amazing, you feel amazing. Step out with confidence and turn heads effortlessly.",
				},
			],
		},
		reasonsToLove: {
			heading: "[4 Reasons] Why You Will Love It:",
			items: [
				{
					heading: "Unlock Effortless Style",
					content:
						"Imagine a world where good hair days are every day. This brush gives you the power to create salon-worthy blowouts effortlessly, transforming your look with ease and speed.",
				},
				{
					heading: "Embrace Radiant Shine",
					content:
						"Watch as your hair transforms from dull to dazzling. The advanced technology polishes each strand, leaving you with breathtaking shine that turns heads.",
				},
				{
					heading: "Experience Touchably Soft Hair",
					content:
						"Feel the difference as frizz and static disappear, replaced by hair that's incredibly soft and smooth to the touch. Your hair will thank you.",
				},
				{
					heading: "Reclaim Your Confidence",
					content:
						"When your hair looks sensational, your confidence soars. Step out feeling empowered, knowing you look your absolute best, every single day.",
				},
			],
		},
		comparison: {
			heading: "What Makes Us [Different]",
			content:
				"Unlike traditional styling tools that dry out your hair, this brush infuses moisture and shine. It's the one-step solution for a salon finish without the damage or time commitment.",
			othersText: "Others",
			items: [
				"1-step dry and style",
				"Reduces frizz and damage",
				"Adds shine and volume",
				"Even heat distribution",
				"Moisture-locking technology",
			],
		},
		faq: {
			heading: "Frequently Asked Questions",
			items: [
				{
					question:
						"Is this hair dryer brush suitable for all hair types?",
					answer: "Yes, with its three heat and speed settings, including a cool option, this hair dryer brush is designed to cater to various hair types. You can customize the settings to achieve the best results for your specific hair texture and length.",
				},
				{
					question:
						"How does the ceramic and ionic technology benefit my hair?",
					answer: "The ceramic titanium barrel ensures even heat distribution, significantly reducing heat damage from over-styling, while negative ions quickly penetrate your hair to lock in moisture. This combination effectively nourishes damaged hair, reduces frizz, and eliminates static, leaving you with noticeably smoother and shinier hair.",
				},
				{
					question:
						"Can I really dry and style my hair in one step with this tool?",
					answer: "Absolutely! This innovative tool combines the power of a hair dryer with the styling capabilities of a brush. You can dry, style, and add volume to your hair simultaneously, streamlining your routine and saving you valuable time.",
				},
				{
					question: "Will this brush help reduce hair damage?",
					answer: "Yes, it's specifically designed to minimize hair damage. The ceramic and ionic technology ensures efficient drying and styling with less frizz, reducing the need for multiple heat tools and protecting your hair from excessive heat exposure.",
				},
				{
					question: "What kind of results can I expect?",
					answer: "You can expect professional salon-quality blowout results right at home. Your hair will be sleek, shiny, voluminous, and free from frizz, giving you that 'just left the salon' look every time you use it.",
				},
				{
					question: "How powerful is the motor for drying?",
					answer: "The hair dryer brush features a powerful 1200W motor. This provides optimal heat for efficiently drying wet hair without causing any damage, making your styling process quick and safe.",
				},
				{
					question: "Is it heavy or difficult to handle?",
					answer: "The design of the brush is ergonomic and lightweight, making it comfortable to hold and easy to maneuver during your styling routine. You'll find it simple to achieve precise control for great results without strain.",
				},
				{
					question: "Does it create a lot of static in the hair?",
					answer: "No, quite the opposite! The ionic technology incorporated into the brush actively works to reduce static. This ensures your hair remains smooth and manageable, preventing those annoying flyaways and promoting a polished finish.",
				},
			],
		},
		reviews: {
			averageRating: 4.8,
			basedOn: "Based on <amount> reviews",
			count: 557,
			heading: "What our customers say about us",
			loadMore: "Load more",
			verifiedBuyer: "Verified customer",
			voteText: "Was this helpful?",
			items: [
				{
					authorName: "Faith N",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/7.jpeg",
					title: "My New Holy Grail",
					content:
						"I can't believe how much time this saves me! My hair used to be a frizzy mess after drying, but now it's smooth and shiny. Seriously, it's a game-changer for my morning routine.",
					stars: 5,
					date: "July 2025",
					likes: 30,
					dislikes: 9,
				},
				{
					authorName: "Vanessa K",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/30.jpeg",
					title: "So Easy to Use!",
					content:
						"I'm not great at styling my hair, but this brush makes it so simple. My hair actually looks like I got a blowout, and it lasts all day. Highly recommend if you struggle with hair tools.",
					stars: 5,
					date: "August 2025",
					likes: 36,
					dislikes: 5,
				},
				{
					authorName: "Frances M",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/44.jpeg",
					title: "Good, But Takes Practice",
					content:
						"It works as advertised, gives good volume. My arm did get a bit tired at first, but I'm getting the hang of it. My hair does feel softer than with my old dryer.",
					stars: 4,
					date: "July 2025",
					likes: 87,
					dislikes: 9,
				},
				{
					authorName: "Jennifer A",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/26.jpeg",
					title: "Finally, Less Frizz!",
					content:
						"My biggest problem is frizz, especially in humidity. This dryer brush really helps keep it under control. My hair feels much healthier now, not as dried out.",
					stars: 4,
					date: "August 2025",
					likes: 68,
					dislikes: 2,
				},
				{
					authorName: "Sheri R",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/14.jpeg",
					title: "Impressive Shine!",
					content:
						"I love the shine this gives my hair! I've been looking for something to make my hair look less dull, and this totally delivered. It's really gentle on my fine hair too.",
					stars: 5,
					date: "September 2025",
					likes: 11,
					dislikes: 9,
				},
				{
					authorName: "Omar F",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/male/6.jpeg",
					title: "Works for Men's Hair Too",
					content:
						"I use it for my medium-length hair to add some volume. It gives a nice, natural look without being too much. Pretty quick for a guy's routine.",
					stars: 4,
					date: "December 2025",
					likes: 23,
					dislikes: 3,
				},
				{
					authorName: "Deborah G",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/18.jpeg",
					title: "Better Than Expected",
					content:
						"I bought this on a whim, didn't expect much. But wow, my hair looks so much better! The volume is incredible, and it actually doesn't feel fried. Loving it.",
					stars: 5,
					date: "October 2025",
					likes: 61,
					dislikes: 5,
				},
				{
					authorName: "Elvira H",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/39.jpeg",
					title: "A Little Bulky for Travel",
					content:
						"It's fantastic for everyday use at home. But I tried taking it on a trip, and it's a bit larger than I'd like for my suitcase. Performance-wise, it's excellent.",
					stars: 4,
					date: "November 2025",
					likes: 67,
					dislikes: 1,
				},
				{
					authorName: "Julia P",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/16.jpeg",
					title: "My Go-To Styler",
					content:
						"This is the only tool I need now. It dries my hair fast and smooths out all the kinks. I get so many compliments on my hair after using this, it's amazing.",
					stars: 5,
					date: "July 2025",
					likes: 58,
					dislikes: 7,
				},
				{
					authorName: "Molly C",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/42.jpeg",
					title: "Good Value for Money",
					content:
						"Considering how much I'd spend at the salon for a blowout, this pays for itself quickly. It's a solid tool, does what it says, no complaints.",
					stars: 4,
					date: "August 2025",
					likes: 11,
					dislikes: 3,
				},
				{
					authorName: "Norma W",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/36.jpeg",
					title: "Wish It Had More Heat Settings",
					content:
						"It's good, but sometimes I feel like I need a slightly lower heat. My hair is very fine. Still, the results are shiny, and it's fast.",
					stars: 3,
					date: "December 2025",
					likes: 67,
					dislikes: 1,
				},
				{
					authorName: "Christie R",
					authorPhotoSrc:
						"https://pages-staging.kopy.app/public/images/female/35.jpeg",
					title: "Healthy Hair, Happy Me",
					content:
						"My hair feels so much healthier since using this. The ceramic technology really seems to make a difference. I don't dread hair washing days anymore; I look forward to styling!",
					stars: 5,
					date: "August 2025",
					likes: 37,
					dislikes: 3,
				},
			],
		},
		footer: {
			shopFeatures: {
				shippingInformation: {
					heading: "Free & Fast Shipping",
					content: "Doorstep delivery worldwide.",
				},
				returnPolicy: {
					heading: "Hassle-free exchange",
					content: "No questions asked",
				},
				priceMatchGuarantee: {
					heading: "Price-match guarantee",
					content: "Save money when ordering with us",
				},
				customerSatisfaction: {
					heading: "5-Star Reviews",
					content: "Customer satisfaction is our #1 priority",
				},
			},
			emailSignup: {
				heading: "Subscribe to our emails",
				subheading:
					"Be the first to know about new collections and exclusive offers.",
			},
			customerSupport: {
				heading: "Need To Get In Touch?",
				content:
					'<p>If you have any queries, please do not hesitate to contact us. We aim to reply within 48 hours.<br/><br/>Please note that our Customer Service opening times are 9am-5pm, Monday to Friday. <br/><br/>For general or customer enquiries please use this contact form: <a href="/pages/contact" title="Contact">Contact Us</a></p>',
			},
		},
		ui: {
			addToCartButton: "ADD TO CART",
			marquee: "FREE SHIPPING ON ALL ORDERS",
			emailPlaceholder: "Email",
		},
		homepage: {
			hero: {
				ratingText: "557 reviews",
				heading: "Hair Perfection in Minutes",
				subheading: "Your All-in-One Salon Blowout at Home",
				buttonLabel: "ADD TO CART",
			},
			featuredIn: {
				heading: "As Featured In",
			},
			secondaryHero: {
				heading: "[4 Reasons] Why You Will Love It:",
				text: "Unlike traditional styling tools that dry out your hair, this brush infuses moisture and shine. It's the one-step solution for a salon finish without the damage or time commitment.",
				buttonLabel: "ADD TO CART",
				features: [
					"Effortless Salon Blowout at Home",
					"Reveal Your Hair's Natural Radiance",
					"Reclaim Your Mornings",
				],
			},
			testimonial: {
				heading: "My New Holy Grail",
				text: "I can't believe how much time this saves me! My hair used to be a frizzy mess after drying, but now it's smooth and shiny. Seriously, it's a game-changer for my morning routine.",
			},
			comparison: {
				heading: "What Makes Us [Different]",
				text: "Unlike traditional styling tools that dry out your hair, this brush infuses moisture and shine. It's the one-step solution for a salon finish without the damage or time commitment.",
				othersLabel: "Others",
				items: [
					"1-step dry and style",
					"Reduces frizz and damage",
					"Adds shine and volume",
					"Even heat distribution",
					"Moisture-locking technology",
				],
			},
			faq: {
				caption:
					"Find quick answers to the most common questions about our product.",
				heading: "Frequently Asked Questions",
				items: [
					{
						question:
							"Is this hair dryer brush suitable for all hair types?",
						answer: "Yes, with its three heat and speed settings, including a cool option, this hair dryer brush is designed to cater to various hair types. You can customize the settings to achieve the best results for your specific hair texture and length.",
					},
					{
						question:
							"How does the ceramic and ionic technology benefit my hair?",
						answer: "The ceramic titanium barrel ensures even heat distribution, significantly reducing heat damage from over-styling, while negative ions quickly penetrate your hair to lock in moisture. This combination effectively nourishes damaged hair, reduces frizz, and eliminates static, leaving you with noticeably smoother and shinier hair.",
					},
					{
						question:
							"Can I really dry and style my hair in one step with this tool?",
						answer: "Absolutely! This innovative tool combines the power of a hair dryer with the styling capabilities of a brush. You can dry, style, and add volume to your hair simultaneously, streamlining your routine and saving you valuable time.",
					},
					{
						question: "Will this brush help reduce hair damage?",
						answer: "Yes, it's specifically designed to minimize hair damage. The ceramic and ionic technology ensures efficient drying and styling with less frizz, reducing the need for multiple heat tools and protecting your hair from excessive heat exposure.",
					},
				],
			},
			beforeAfter: {
				heading: "Transform Your Hair, Transform You",
				content:
					"Unlike traditional styling tools that dry out your hair, this brush infuses moisture and shine. It's the one-step solution for a salon finish without the damage or time commitment.",
				buttonLabel: "ADD TO CART",
				beforeLabel: "Before",
				afterLabel: "After",
			},
		},
		options: [
			{
				name: "Color",
				values: [
					{ name: "Black", originalName: "Black" },
					{ name: "Pink", originalName: "Pink" },
				],
			},
		],
	},
}

export default Pages
